import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  getScenarios, createScenario, getScenario, updateScenario, deleteScenario,
  addScenarioItem, updateScenarioItem, deleteScenarioItem, getScenarioProjection,
} from '../../api/client';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmt = (n) => `${n < 0 ? '-' : ''}$${Math.abs(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthKeyLabel = (key) => { const [y, m] = String(key).split('-'); return `${MONTHS[+m - 1]} ${y}`; };
// The user's local date — sent to the server, whose UTC clock may already be on tomorrow.
const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

// ─── Scenario detail (projection + items) ────────────────────────────────────
const ScenarioDetail = ({ id, onBack }) => {
  const { COLORS } = useTheme();
  const [scenario, setScenario]     = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [lens, setLens]             = useState('year');   // 'year' | 'month'
  const [focusKey, setFocusKey]     = useState(null);
  const [savingVar, setSavingVar]   = useState(false);
  const [itemKind, setItemKind]     = useState('expense');
  const [itemLabel, setItemLabel]   = useState('');
  const [itemAmount, setItemAmount] = useState('');
  const [itemMonth, setItemMonth]   = useState('');
  const [adding, setAdding]         = useState(false);
  const [editKey, setEditKey]       = useState(null);   // which worksheet line is being adjusted, e.g. 'exp-12'
  const [editAmount, setEditAmount] = useState('');
  const [editScope, setEditScope]   = useState('onward');
  const [savingOverride, setSavingOverride] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        getScenario(id),
        getScenarioProjection(id, { months: 12, today: localToday() }),
      ]);
      setScenario(sRes.data);
      setProjection(pRes.data);
      const firstKey = pRes.data.periods?.[0]?.key;
      if (firstKey) {
        setFocusKey((prev) => prev || firstKey);
        setItemMonth((prev) => prev || firstKey);
      }
    } catch (e) { /* handled by empty UI */ }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const refreshProjection = async () => {
    try { const p = await getScenarioProjection(id, { months: 12, today: localToday() }); setProjection(p.data); } catch (e) {}
  };

  const toggleVariable = async () => {
    setSavingVar(true);
    try {
      const r = await updateScenario(id, { include_variable: !scenario.include_variable });
      setScenario((prev) => ({ ...prev, include_variable: r.data.include_variable }));
      await refreshProjection();
    } catch (e) {} finally { setSavingVar(false); }
  };

  const handleAddItem = async () => {
    if (!itemLabel.trim() || !itemMonth) return;
    setAdding(true);
    try {
      await addScenarioItem(id, { kind: itemKind, label: itemLabel.trim(), amount: parseFloat(itemAmount || 0), month: itemMonth });
      setItemLabel(''); setItemAmount('');
      await load();
    } catch (e) {} finally { setAdding(false); }
  };

  const handleDeleteItem = async (itemId) => {
    try { await deleteScenarioItem(id, itemId); await load(); } catch (e) {}
  };

  const handleDeleteScenario = async () => {
    if (!window.confirm('Delete this plan and all its items?')) return;
    try { await deleteScenario(id); onBack(); } catch (e) {}
  };

  // ── line overrides (tap a worksheet line to adjust it within this plan) ──
  const openEditor = (line, kindKey, focusKeyNow) => {
    const k = `${kindKey}-${line.id}`;
    if (editKey === k) { setEditKey(null); return; }   // tap again to close
    setEditKey(k);
    setEditAmount(String(line.amount));
    // Editing the override that starts THIS month keeps its scope; otherwise
    // default to 'onward' (cancel/raise is the common case).
    setEditScope(line.overridden && line.overrideFrom === focusKeyNow ? line.overrideScope : 'onward');
  };

  const saveOverride = async (line, kindKey, focusKeyNow) => {
    setSavingOverride(true);
    try {
      const amount = parseFloat(editAmount || 0);
      if (line.overridden && line.overrideFrom === focusKeyNow) {
        // This month already starts an override for the line — edit it in place.
        await updateScenarioItem(id, line.overrideId, { amount, scope: editScope });
      } else {
        // Otherwise layer a new override starting this month (last writer wins).
        await addScenarioItem(id, {
          kind: 'override', label: line.name, amount, month: focusKeyNow, scope: editScope,
          ...(kindKey === 'inc' ? { income_source_id: line.id } : { category_id: line.id }),
        });
      }
      setEditKey(null);
      await load();
    } catch (e) {} finally { setSavingOverride(false); }
  };

  const removeOverride = async (line) => {
    setSavingOverride(true);
    try { await deleteScenarioItem(id, line.overrideId); setEditKey(null); await load(); }
    catch (e) {} finally { setSavingOverride(false); }
  };

  if (loading || !scenario || !projection) {
    return <div style={{ padding: 24, color: COLORS.textMuted, fontFamily: "'DM Sans', sans-serif" }}>Loading…</div>;
  }

  const inp = { padding: '9px 10px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.text, fontSize: 13, fontFamily: 'inherit', outline: 'none' };
  const netColor = (n) => (n < 0 ? COLORS.red : COLORS.green);

  // 'YYYY-MM-DD' → 'Jul 26'. String-split on purpose: new Date('2026-07-26') parses
  // as UTC midnight, which can render as the previous day in local time.
  const mdLabel = (s) => { const [, m, d] = String(s).split('-'); return `${MONTHS[+m - 1]} ${+d}`; };
  const SCHEDULE_LABELS = { monthly: 'monthly', twiceMonthly: 'twice monthly', weekly: 'weekly', biweekly: 'biweekly' };

  // One worksheet line: label + muted annotation on the left, amount on the right.
  // `base` (the pre-adjustment amount) renders struck through before the value.
  const LineRow = ({ label, sub, value, color, base, onClick, active }) => (
    <div
      onClick={onClick}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: `1px solid ${COLORS.border}`, cursor: onClick ? 'pointer' : 'default', background: active ? COLORS.inputBg : 'transparent' }}
    >
      <span style={{ color: COLORS.text, fontSize: 13, fontWeight: 500 }}>
        {label}{sub && <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 400 }}> {sub}</span>}
      </span>
      <span style={{ color: color || COLORS.text, fontFamily: 'monospace', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
        {base != null && <span style={{ color: COLORS.textMuted, textDecoration: 'line-through', fontWeight: 400, marginRight: 6 }}>{base}</span>}
        {value}
      </span>
    </div>
  );

  // Inline adjust panel, rendered directly under the tapped line. Deliberately a
  // plain function call (not a <Component>) so React doesn't remount it — and drop
  // input focus — every time a keystroke re-renders ScenarioDetail.
  const renderOverrideEditor = (line, kindKey) => (
    <div style={{ padding: '12px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: COLORS.textMuted, fontSize: 12 }}>Adjust to</span>
        <input
          type="number" value={editAmount} autoFocus
          onChange={(e) => setEditAmount(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !savingOverride && editAmount !== '' && saveOverride(line, kindKey, focus.key)}
          style={{ ...inp, width: 110, background: COLORS.surface }}
        />
        <span style={{ color: COLORS.textMuted, fontSize: 11 }}>normally {fmt(line.baseAmount != null ? line.baseAmount : line.amount)} · 0 = skip/cancel</span>
      </div>
      <div style={{ display: 'flex', gap: 2, background: COLORS.surface, padding: 3, borderRadius: 8, border: `1px solid ${COLORS.border}`, alignSelf: 'flex-start' }}>
        {[['month', 'Just this month'], ['onward', 'From this month on']].map(([v, lab]) => (
          <button key={v} onClick={() => setEditScope(v)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, background: editScope === v ? COLORS.accent : 'transparent', color: editScope === v ? '#fff' : COLORS.textMuted }}>{lab}</button>
        ))}
      </div>
      {line.overridden && line.overrideFrom !== focus.key && (
        <div style={{ color: COLORS.textMuted, fontSize: 11, lineHeight: 1.5 }}>
          Adjusted since {monthKeyLabel(line.overrideFrom)} ({line.overrideScope === 'onward' ? 'onward' : 'that month only'}). Saving layers a new change starting {focus.label}.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => saveOverride(line, kindKey, focus.key)} disabled={savingOverride || editAmount === ''} style={{ background: COLORS.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: savingOverride || editAmount === '' ? 0.6 : 1 }}>Save</button>
        <button onClick={() => setEditKey(null)} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        {line.overridden && (
          <button onClick={() => removeOverride(line)} disabled={savingOverride} style={{ marginLeft: 'auto', background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.red, borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Remove adjustment</button>
        )}
      </div>
    </div>
  );

  // A group divider inside a section (a week, or "anytime") with its own subtotal.
  const GroupHeader = ({ label, subtotal }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}` }}>
      <span style={{ color: COLORS.textMuted, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>{label}</span>
      <span style={{ color: COLORS.textMuted, fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700 }}>{subtotal}</span>
    </div>
  );

  // A worksheet section (Income / Expenses) with its subtotal in the header.
  const SectionCard = ({ title, subtotal, subtotalColor, children }) => (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: COLORS.inputBg, borderBottom: `1px solid ${COLORS.border}` }}>
        <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>{title}</span>
        <span style={{ color: subtotalColor || COLORS.text, fontFamily: 'monospace', fontSize: 13, fontWeight: 800 }}>{subtotal}</span>
      </div>
      {children}
    </div>
  );

  const periods    = projection.periods || [];
  const focus      = periods.find((p) => p.key === focusKey) || periods[0];
  const focusIndex = periods.findIndex((p) => p.key === focus?.key);
  const items      = (scenario.items || []).filter((it) => it.kind !== 'override'); // overrides live in the worksheet
  const monthItems = focus ? items.filter((it) => it.month_key === focus.key) : [];
  const shortfall  = focus && focus.net < 0 ? Math.abs(focus.net) : 0;
  const perMonth   = shortfall && focusIndex > 0 ? shortfall / focusIndex : 0;

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 10, padding: 0 }}>‹ All plans</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12 }}>
        <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>{scenario.name}</h2>
        <button onClick={handleDeleteScenario} style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.red, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Delete plan</button>
      </div>

      {/* controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, background: COLORS.inputBg, padding: 3, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
          {['year', 'month'].map((l) => (
            <button key={l} onClick={() => setLens(l)} style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, textTransform: 'capitalize', background: lens === l ? COLORS.accent : 'transparent', color: lens === l ? '#fff' : COLORS.textMuted }}>{l}</button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: COLORS.text, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={scenario.include_variable} onChange={toggleVariable} disabled={savingVar} />
          Include variable spending
        </label>
      </div>

      {/* YEAR LENS */}
      {lens === 'year' && (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 22 }}>
          {periods.map((p) => (
            <button key={p.key} onClick={() => { setFocusKey(p.key); setLens('month'); }} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: `1px solid ${COLORS.border}`, padding: '11px 14px', cursor: 'pointer', fontFamily: 'inherit', display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8 }}>
              <span style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>
                {p.label}{p.itemCount > 0 && <span style={{ color: COLORS.accent, fontSize: 11, fontWeight: 700 }}> · {p.itemCount} one-off{p.itemCount === 1 ? '' : 's'}</span>}
              </span>
              <span style={{ color: netColor(p.net), fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{fmt(p.net)}</span>
            </button>
          ))}
          <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, background: COLORS.inputBg }}>
            <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>12-month net</span>
            <span style={{ color: netColor(projection.totals.net), fontFamily: 'monospace', fontSize: 15, fontWeight: 800 }}>{fmt(projection.totals.net)}</span>
          </div>
        </div>
      )}

      {/* MONTH LENS — line-item worksheet */}
      {lens === 'month' && focus && (
        <div style={{ marginBottom: 22 }}>
          <select value={focus.key} onChange={(e) => setFocusKey(e.target.value)} style={{ width: '100%', ...inp, padding: '10px 12px', fontSize: 14, marginBottom: 14 }}>
            {periods.map((p) => <option key={p.key} value={p.key}>{p.label}  ({p.start} – {p.end})</option>)}
          </select>

          <SectionCard title="Income" subtotal={fmt(focus.projectedIncome)} subtotalColor={COLORS.green}>
            {(focus.incomeLines || []).map((l) => (
              <div key={`inc-${l.id}`}>
                <LineRow
                  label={l.name}
                  sub={`· ${SCHEDULE_LABELS[l.scheduleType] || l.scheduleType}${l.overridden ? ' · adjusted' : ''}`}
                  value={fmt(l.amount)}
                  color={l.overridden ? COLORS.accent : undefined}
                  base={l.overridden ? fmt(l.baseAmount) : null}
                  onClick={() => openEditor(l, 'inc', focus.key)}
                  active={editKey === `inc-${l.id}`}
                />
                {editKey === `inc-${l.id}` && renderOverrideEditor(l, 'inc')}
              </div>
            ))}
            {monthItems.filter((it) => it.kind === 'income').map((it) => (
              <LineRow key={`item-${it.id}`} label={it.label} sub="· planned one-off" value={`+${fmt(parseFloat(it.amount))}`} color={COLORS.green} />
            ))}
            {(focus.incomeLines || []).length === 0 && monthItems.filter((it) => it.kind === 'income').length === 0 && (
              <div style={{ padding: '10px 14px', color: COLORS.textMuted, fontSize: 12 }}>No recurring income sources set up.</div>
            )}
          </SectionCard>

          <SectionCard title="Expenses" subtotal={fmt(focus.projectedExpenses)}>
            {(focus.weeks || []).map((w) => {
              const weekLines = (focus.expenseLines || []).filter((l) => l.week === w.index);
              if (weekLines.length === 0) return null;
              return (
                <div key={`w-${w.index}`}>
                  <GroupHeader label={`Week ${w.index} · ${mdLabel(w.start)} – ${mdLabel(w.end)}`} subtotal={fmt(weekLines.reduce((s, l) => s + l.amount, 0))} />
                  {weekLines.map((l) => (
                    <div key={`exp-${l.id}`}>
                      <LineRow
                        label={l.name}
                        sub={`· ${mdLabel(l.dueDate)}${l.overridden ? ' · adjusted' : ''}`}
                        value={fmt(l.amount)}
                        color={l.overridden ? COLORS.accent : undefined}
                        base={l.overridden ? fmt(l.baseAmount) : null}
                        onClick={() => openEditor(l, 'exp', focus.key)}
                        active={editKey === `exp-${l.id}`}
                      />
                      {editKey === `exp-${l.id}` && renderOverrideEditor(l, 'exp')}
                    </div>
                  ))}
                </div>
              );
            })}
            {(() => {
              const anytime = (focus.expenseLines || []).filter((l) => l.week == null);
              const oneOffs = monthItems.filter((it) => it.kind === 'expense');
              if (anytime.length === 0 && oneOffs.length === 0) {
                return (focus.expenseLines || []).length === 0 ? (
                  <div style={{ padding: '10px 14px', color: COLORS.textMuted, fontSize: 12 }}>No budget categories in this forecast.</div>
                ) : null;
              }
              const subtotal = anytime.reduce((s, l) => s + l.amount, 0) + oneOffs.reduce((s, it) => s + parseFloat(it.amount || 0), 0);
              return (
                <div>
                  <GroupHeader label="Anytime this month" subtotal={fmt(subtotal)} />
                  {anytime.map((l) => (
                    <div key={`exp-${l.id}`}>
                      <LineRow
                        label={l.name}
                        sub={`· ${l.type}${l.overridden ? ' · adjusted' : ''}`}
                        value={fmt(l.amount)}
                        color={l.overridden ? COLORS.accent : undefined}
                        base={l.overridden ? fmt(l.baseAmount) : null}
                        onClick={() => openEditor(l, 'exp', focus.key)}
                        active={editKey === `exp-${l.id}`}
                      />
                      {editKey === `exp-${l.id}` && renderOverrideEditor(l, 'exp')}
                    </div>
                  ))}
                  {oneOffs.map((it) => (
                    <LineRow key={`item-${it.id}`} label={it.label} sub="· planned one-off" value={fmt(parseFloat(it.amount))} color={COLORS.amber} />
                  ))}
                </div>
              );
            })()}
          </SectionCard>

          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '13px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Projected net</span>
            <span style={{ color: netColor(focus.net), fontFamily: 'monospace', fontSize: 16, fontWeight: 800 }}>{fmt(focus.net)}</span>
          </div>

          {focus.net < 0 && focusIndex > 0 && (
            <div style={{ background: COLORS.amberGlow || COLORS.redGlow, border: `1px solid ${COLORS.amber}44`, borderRadius: 12, padding: '12px 16px', marginTop: 12, color: COLORS.text, fontSize: 13, lineHeight: 1.5 }}>
              To cover the <b>{fmt(shortfall)}</b> shortfall in {focus.label}, set aside <b>{fmt(perMonth)}/month</b> for the next {focusIndex} month{focusIndex === 1 ? '' : 's'}.
            </div>
          )}
        </div>
      )}

      {/* ITEMS */}
      <h3 style={{ color: COLORS.text, fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>Planned one-offs</h3>
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={itemKind} onChange={(e) => setItemKind(e.target.value)} style={{ ...inp, flex: 1 }}>
            <option value="expense">Expense</option>
            <option value="income">Income</option>
          </select>
          <input type="month" value={itemMonth} onChange={(e) => setItemMonth(e.target.value)} style={{ ...inp, flex: 1 }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={itemLabel} onChange={(e) => setItemLabel(e.target.value)} placeholder="Label (e.g. Cancún flights)" style={{ ...inp, flex: 2 }} />
          <input value={itemAmount} onChange={(e) => setItemAmount(e.target.value)} placeholder="Amount" type="number" style={{ ...inp, flex: 1 }} />
        </div>
        <button onClick={handleAddItem} disabled={adding || !itemLabel.trim() || !itemMonth} style={{ background: COLORS.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: adding || !itemLabel.trim() || !itemMonth ? 0.6 : 1 }}>Add to plan</button>
      </div>
      {items.length === 0 ? (
        <p style={{ color: COLORS.textMuted, fontSize: 13 }}>No one-offs yet — add a vacation, a big bill, or extra income above.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map((it) => (
            <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '10px 12px' }}>
              <div>
                <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{it.label}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>{monthKeyLabel(it.month_key)} · {it.kind}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: it.kind === 'income' ? COLORS.green : COLORS.amber, fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{it.kind === 'income' ? '+' : ''}{fmt(parseFloat(it.amount))}</span>
                <button onClick={() => handleDeleteItem(it.id)} aria-label="Delete" style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Scenario list ───────────────────────────────────────────────────────────
const Planning = () => {
  const { COLORS } = useTheme();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [newName, setNewName]     = useState('');
  const [creating, setCreating]   = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { const r = await getScenarios(); setScenarios(r.data || []); } catch (e) {}
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const r = await createScenario({ name: newName.trim() });
      setNewName('');
      await loadList();
      setSelectedId(r.data.id);
    } catch (e) {} finally { setCreating(false); }
  };

  if (selectedId) {
    return <ScenarioDetail id={selectedId} onBack={() => { setSelectedId(null); loadList(); }} />;
  }

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <h2 style={{ color: COLORS.text, fontSize: 24, fontWeight: 800, margin: '0 0 4px', letterSpacing: -0.5 }}>Planning</h2>
      <p style={{ color: COLORS.textMuted, fontSize: 14, margin: '0 0 20px' }}>Save "what-if" plans for upcoming months — vacations, big expenses, extra income.</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="New plan name (e.g. December – Cancún)"
          style={{ flex: 1, padding: '10px 14px', background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, borderRadius: 10, color: COLORS.text, fontSize: 14, fontFamily: 'inherit', outline: 'none' }}
        />
        <button onClick={handleCreate} disabled={creating || !newName.trim()} style={{ background: COLORS.accent, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', opacity: creating || !newName.trim() ? 0.6 : 1 }}>+ New plan</button>
      </div>

      {loading ? (
        <p style={{ color: COLORS.textMuted }}>Loading…</p>
      ) : scenarios.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: COLORS.textMuted }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🗓️</div>
          No plans yet. Create one above to start forecasting.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => setSelectedId(s.id)} style={{ textAlign: 'left', background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: '14px 16px', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: COLORS.text, fontSize: 15, fontWeight: 700 }}>{s.name}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 3 }}>
                  {s.item_count} one-off{s.item_count === 1 ? '' : 's'} · variable {s.include_variable ? 'on' : 'off'}
                </div>
              </div>
              <span style={{ color: COLORS.textMuted, fontSize: 20 }}>›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Planning;
